<script lang="ts">
  import AutoComplete from '$shared/atoms/AutoComplete/AutoComplete.svelte';
  import Dropdown from '$shared/atoms/Dropdown/Dropdown.svelte';
  import Input from '$shared/atoms/Input/Input.svelte';
  import InputGroup from '$shared/atoms/InputGroup/InputGroup.svelte';
  import Label from '$provider/constants/Label';
  import AddressType, { getAddressTypeAsList } from '$provider/lib/apis/models/AddressType';
  import { schemaData } from '$shared/lib/data/SchemaData';
  import type { IProfileMergeRecordAddressModel } from '$provider/lib/apis/interfaces/IProfileMergeRecordAddressModel';
  import { countryData } from '$provider/lib/datas/CountryData';
  import { AddressValidator } from '$shared/helpers/validators/AddressValidator';
  import { onMount } from 'svelte';

  const stateList = schemaData.getStateAsCommonlist();
  const countryList = countryData.getAsCommonList();
  const addressTypeList = getAddressTypeAsList();

  interface Props {
    value?: IProfileMergeRecordAddressModel | null;
    showType?: boolean;
    showCountry?: boolean;
    onchange?: (formData: IProfileMergeRecordAddressModel) => void;
  }

  let { value = null, showType = true, showCountry = false, onchange }: Props = $props();

  export const isValid = (): boolean => {
    try {
      AddressValidator.validateProfileMergeAddress(formData);
      return true;
    } catch {
      return false;
    }
  };

  let formData: IProfileMergeRecordAddressModel = $state({
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    countryId: undefined,
    type: AddressType.Work
  });

  let selectedStateLabel: string = $state();
  let selectedCountryLabel: string = $state();

  onMount(() => {
    value && (formData = value);
    formData.state &&
      (selectedStateLabel = stateList.find((item) => item.id === formData.state)?.label || '');
    formData.countryId && (selectedCountryLabel = countryData.getLabel(formData.countryId));
  });

  const dispatchChange = (): void => {
    onchange?.(formData);
  };

  const onInputChange = (key: keyof IProfileMergeRecordAddressModel, detail: any): void => {
    formData[key] = detail?.value?.trim();
    dispatchChange();
  };

  const onStateSelect = (detail: CommonList): void => {
    selectedStateLabel = detail.label;
    formData.state = detail.id;
    dispatchChange();
  };

  const onStateCleared = (): void => {
    selectedStateLabel = '';
    formData.state = '';
    dispatchChange();
  };

  const onCountrySelect = (detail: CommonList): void => {
    selectedCountryLabel = detail.label;
    formData.countryId = detail.id;
    dispatchChange();
  };

  const onCountryCleared = (): void => {
    selectedCountryLabel = '';
    formData.countryId = '';
    dispatchChange();
  };

  const onTypeSelect = (detail: CommonList): void => {
    formData.type = detail.id;
    dispatchChange();
  };
</script>

<div class="address-input">
  <div class="input-wrap">
    <Input
      placeholder="Address Line 1"
      value={formData.addressLine1 || ''}
      oninput={(e) => onInputChange('addressLine1', e)}
    />
  </div>
  <div class="input-wrap">
    <Input
      placeholder="Address Line 2 (optinal)"
      value={formData.addressLine2 || ''}
      oninput={(e) => onInputChange('addressLine2', e)}
    />
  </div>
  <div class="input-wrap">
    <InputGroup columns={3}>
      <Input
        placeholder="City"
        value={formData.city || ''}
        oninput={(e) => onInputChange('city', e)}
      />
      <AutoComplete
        list={stateList}
        value={selectedStateLabel}
        placeholder={Label.State}
        onclick={onStateSelect}
        oncleared={onStateCleared}
      />

      <Input
        placeholder="Zip"
        value={formData.zipCode || ''}
        oninput={(e) => onInputChange('zipCode', e)}
      />
    </InputGroup>
  </div>

  {#if showCountry || showType}
    <div class="input-wrap">
      <InputGroup>
        {#if showCountry}
          <AutoComplete
            list={countryList}
            value={selectedCountryLabel}
            placeholder={Label.Country}
            onclick={onCountrySelect}
            oncleared={onCountryCleared}
          />
        {/if}
        {#if showType}
          <Dropdown list={addressTypeList} label={formData.type} onchange={onTypeSelect} />
        {/if}
      </InputGroup>
    </div>
  {/if}
</div>

<style>
  .input-wrap {
    margin-bottom: 8px;
  }
</style>
